import { useEffect } from 'react';
import { ReactEditor } from 'slate-react';
import { Element, Node, NodeEntry } from 'slate';
import { CustomEditor } from 'slate/types/slate';

import { useEditorUiSetters } from 'app/state/contexts/EditorUiContext';

/**
 * This hook is used to keep track of the selection in the editor. Used by comments.
 */
const useSelection = (editor: CustomEditor, shadowHost: HTMLElement | null | undefined) => {
  const { setSelectionId } = useEditorUiSetters();

  useEffect(() => {
    const shadowRoot = shadowHost?.shadowRoot;
    if (!shadowRoot) {
      return;
    }

    const mouseUpHandler = (event: Event) => {
      if (event.type === 'blur') {
        setSelectionId(null);
        return;
      }

      const root = shadowRoot as ShadowRoot & { getSelection?: () => Selection | null };
      const selection = root.getSelection ? root.getSelection() : root.ownerDocument.getSelection();

      // Selection.isCollapsed does not work in chromium
      // https://bugs.chromium.org/p/chromium/issues/detail?id=447523
      if (!selection || !selection.anchorNode || selection.anchorOffset === selection.focusOffset) {
        setSelectionId(null);
        return;
      }

      let entry: NodeEntry<Node> | undefined;
      try {
        const slateNode = ReactEditor.toSlateNode(editor, selection.anchorNode);
        const slatePath = ReactEditor.findPath(editor, slateNode);
        entry = Array.from(Node.levels(editor, slatePath, { reverse: true })).find(([node]) => Element.isElement(node) && node.uuid);
      } catch {
        setSelectionId(null);
        return;
      }

      if (!entry) {
        setSelectionId(null);
        return;
      }
      const [entryNode] = entry;
      setSelectionId(Element.isElement(entryNode) ? entryNode.uuid ?? null : null);
    };

    const events = ['mouseup', 'keyup'];

    const options = {
      capture: true,
    };

    events.forEach((e) => shadowRoot.addEventListener(e, mouseUpHandler, options));
    events.forEach((e) => window.addEventListener(e, mouseUpHandler, options));
    return () => {
      if (shadowRoot) {
        events.forEach((e) => shadowRoot.removeEventListener(e, mouseUpHandler, options));
      }
      events.forEach((e) => window.removeEventListener(e, mouseUpHandler, options));
    };
  }, [editor, shadowHost, setSelectionId]);
};

export default useSelection;
